import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "../../lib/api";
import { toast } from "sonner";
import { Save } from "lucide-react";

export default function AdminImpact() {
  const [stats, setStats] = useState([]);
  const load = async () => { const r = await api.get("/impact-stats"); setStats(r.data); };
  useEffect(() => { document.title = "Admin · Impact"; load(); }, []);

  const update = (id, key, val) => setStats((s) => s.map((x) => x.id === id ? { ...x, [key]: val } : x));

  const save = async (s) => {
    try {
      await api.put(`/admin/impact-stats/${s.id}`, { label: s.label, value: Number(s.value), suffix: s.suffix, order: Number(s.order) });
      toast.success("Saved");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  return (
    <div data-testid="admin-impact">
      <h1 className="font-serif text-3xl font-bold mb-2">Impact stats</h1>
      <p className="text-[#0a0d12]/60 text-sm mb-6">Update headline numbers shown on Home & Impact pages.</p>
      <Baseline />

      <div className="grid md:grid-cols-2 gap-5">
        {stats.map((s) => (
          <div key={s.id} className="bg-white rounded-xl border border-black/5 p-6" data-testid={`stat-${s.id}`}>
            <div className="grid sm:grid-cols-3 gap-3 items-end">
              <div className="sm:col-span-3">
                <label className="text-xs uppercase tracking-[0.15em] text-[#0d1724]/60 font-semibold mb-1 block">Label</label>
                <input value={s.label} onChange={(e) => update(s.id, "label", e.target.value)} className="w-full px-4 py-2.5 border border-black/15 rounded-md" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.15em] text-[#0d1724]/60 font-semibold mb-1 block">Value</label>
                <input type="number" value={s.value} onChange={(e) => update(s.id, "value", e.target.value)} className="w-full px-4 py-2.5 border border-black/15 rounded-md" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.15em] text-[#0d1724]/60 font-semibold mb-1 block">Suffix</label>
                <input value={s.suffix} onChange={(e) => update(s.id, "suffix", e.target.value)} className="w-full px-4 py-2.5 border border-black/15 rounded-md" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.15em] text-[#0d1724]/60 font-semibold mb-1 block">Order</label>
                <input type="number" value={s.order} onChange={(e) => update(s.id, "order", e.target.value)} className="w-full px-4 py-2.5 border border-black/15 rounded-md" />
              </div>
              <button onClick={() => save(s)} className="dk-btn-primary !py-2.5 sm:col-span-3" data-testid={`save-${s.id}`}>
                <Save size={14} /> Save
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Baseline() {
  const [b, setB] = useState(null);
  useEffect(() => { api.get("/impact-dashboard").then((r) => setB(r.data.verified_baseline)).catch(() => {}); }, []);
  if (!b) return null;
  const save = async () => { try { await api.put("/admin/impact-baseline", { ...b, people_supported: Number(b.people_supported), families_assisted: Number(b.families_assisted), learners_trained: Number(b.learners_trained), funds_deployed_ngn: Number(b.funds_deployed_ngn) }); toast.success("Verified baseline saved — live counters now include it"); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); } };
  const F = ({ k, l }) => <div><label className="text-[10px] uppercase tracking-[0.15em] text-[#0d1724]/60 font-semibold block mb-1">{l}</label><input type="number" value={b[k]} onChange={(e) => setB({ ...b, [k]: e.target.value })} className="w-full px-3 py-2 border border-black/15 rounded-md text-sm" data-testid={`baseline-${k}`} /></div>;
  return (
    <div className="bg-white rounded-xl border border-black/5 p-5 mb-8" data-testid="impact-baseline">
      <h2 className="font-serif text-xl font-bold">Verified pre-platform baseline</h2>
      <p className="text-xs text-[#0a0d12]/60 mb-4">Results achieved before this platform went live (confirmed by the Foundation). Added to the live counters on Home, Donate and Transparency.</p>
      <div className="grid sm:grid-cols-4 gap-3"><F k="people_supported" l="People supported" /><F k="families_assisted" l="Families assisted" /><F k="learners_trained" l="Learners trained" /><F k="funds_deployed_ngn" l="Funds deployed (₦)" /></div>
      <div className="mt-3"><label className="text-[10px] uppercase tracking-[0.15em] text-[#0d1724]/60 font-semibold block mb-1">Verification note</label><input value={b.note || ""} onChange={(e) => setB({ ...b, note: e.target.value })} className="w-full px-3 py-2 border border-black/15 rounded-md text-sm" /></div>
      <button onClick={save} className="dk-btn-primary mt-4 !py-2 text-sm" data-testid="baseline-save"><Save size={14} /> Save baseline</button>
    </div>
  );
}
